import { useMemo, useState, type FormEvent } from "react";
import { useApi } from "../lib/useApi";
import { ApiError, type WorkspaceSummary } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";

// The demo-project-copier service has its own SAMPLE_PROJECT_ID env default,
// but it doesn't match what the silo's CreateDemoProject activity uses on signup
// (DemoProjectCopierSampleProjectId in the silo configmap). To keep the admin
// portal aligned with the signup flow rather than the service's stale Dockerfile
// default, hardcode the same id here and always send it.
const DEFAULT_SAMPLE_PROJECT_ID = "219991f4-5eba-40da-86db-afc564e7c89e";
const OWNER_ROLES = ["Owner", "Admin"];

function resolveDefaultOwner(ws: WorkspaceSummary): string {
  const memberIds = new Set(ws.members.map((m) => m.userId));
  if (ws.createdByUserId && memberIds.has(ws.createdByUserId)) return ws.createdByUserId;
  const byRole = ws.members.find((m) => OWNER_ROLES.includes(m.role));
  if (byRole) return byRole.userId;
  return ws.members[0]?.userId ?? "";
}

function memberLabel(ws: WorkspaceSummary, userId: string): string {
  const m = ws.members.find((x) => x.userId === userId);
  if (!m) return userId;
  return `${m.email ?? m.displayName ?? "(no email)"} · ${m.role}`;
}

export function DeployDemoProjectPage() {
  const api = useApi();
  useSetTopbarTabs([{ id: "copier", label: "Demo project copier", active: true }]);

  const [workspaceIdInput, setWorkspaceIdInput] = useState("");
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null);
  const [loadingWs, setLoadingWs] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [ownerId, setOwnerId] = useState("");
  const [sampleProjectId, setSampleProjectId] = useState("");

  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.runDemoProjectCopier>> | null>(null);

  const creatorMissing = useMemo(
    () => !!workspace && !!workspace.createdByUserId && !workspace.members.some((m) => m.userId === workspace.createdByUserId),
    [workspace],
  );

  async function loadWorkspace(e: FormEvent) {
    e.preventDefault();
    const id = workspaceIdInput.trim();
    if (!id) return;
    setLoadingWs(true);
    setLoadError(null);
    setWorkspace(null);
    setResult(null);
    setRunError(null);
    setConfirming(false);
    try {
      const ws = await api.getWorkspace(id);
      setWorkspace(ws);
      setOwnerId(resolveDefaultOwner(ws));
    } catch (err) {
      setLoadError(err instanceof ApiError && err.status === 404 ? `No workspace found with id ${id}.` : `${err}`);
    } finally {
      setLoadingWs(false);
    }
  }

  async function dispatch() {
    if (!workspace || !ownerId) return;
    setRunning(true);
    setRunError(null);
    try {
      const res = await api.runDemoProjectCopier({
        workspaceId: workspace.id,
        projectOwnerId: ownerId,
        sampleProjectId: sampleProjectId.trim() || DEFAULT_SAMPLE_PROJECT_ID,
      });
      setResult(res);
      setConfirming(false);
    } catch (err) {
      setRunError(`${err}`);
    } finally {
      setRunning(false);
    }
  }

  function reset() {
    setWorkspaceIdInput("");
    setWorkspace(null);
    setLoadError(null);
    setOwnerId("");
    setSampleProjectId("");
    setResult(null);
    setRunError(null);
    setConfirming(false);
  }

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Operation 01 · Demo project copier</div>
        <h1>Deploy demo project</h1>
        <p>
          Dispatch the demo-project-copier job — it clones the standard sample project (DB rows, blobs and markup
          library) into the workspace you specify. The copy runs asynchronously as a Kubernetes job and takes a few
          minutes.
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Target workspace
          <span className="hint">workspace id (GUID)</span>
        </header>
        <div className="body">
          <form className="inline-form" onSubmit={loadWorkspace}>
            <input
              type="text"
              placeholder="00000000-0000-0000-0000-000000000000"
              spellCheck={false}
              autoComplete="off"
              value={workspaceIdInput}
              onChange={(e) => setWorkspaceIdInput(e.target.value)}
            />
            <button className={"btn btn-primary" + (loadingWs ? " loading" : "")} disabled={loadingWs || !workspaceIdInput.trim()}>
              <span className="glyph" /> Load
            </button>
          </form>
          {loadError && (
            <div className="callout error" style={{ marginTop: 16 }}>
              <span className="mk">err</span>
              <span>{loadError}</span>
            </div>
          )}
          {workspace && (
            <>
              <dl className="spec" style={{ marginTop: 18 }}>
                <dt>name</dt>
                <dd>{workspace.name}</dd>
                <dt>workspace id</dt>
                <dd>{workspace.id}</dd>
                <dt>created by</dt>
                <dd>{workspace.createdByUserId ?? <span className="faint">unknown</span>}</dd>
                <dt>members</dt>
                <dd>{workspace.members.length}</dd>
              </dl>
              <div style={{ marginTop: 14, border: "1px solid var(--line)" }}>
                <table className="grid">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>User id</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspace.members.length === 0 && (
                      <tr>
                        <td className="empty" colSpan={3}>
                          workspace has no members
                        </td>
                      </tr>
                    )}
                    {workspace.members.map((m) => (
                      <tr key={m.userId}>
                        <td>{m.email ?? <span className="faint">(none)</span>}</td>
                        <td className="mono">{m.userId}</td>
                        <td>
                          <span className={"tag" + (OWNER_ROLES.includes(m.role) ? " cyan" : "")}>{m.role}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>

      {workspace && (
        <section className="panel reveal" data-d="3">
          <header>
            <span className="idx">02</span> Project owner
            <span className="hint">who will own the copied project</span>
          </header>
          <div className="body">
            {creatorMissing && (
              <div className="callout warn" style={{ marginBottom: 16 }}>
                <span className="mk">note</span>
                <span>
                  The workspace creator (<span className="mono">{workspace.createdByUserId}</span>) is no longer a
                  member. Pick another owner below.
                </span>
              </div>
            )}
            <div className="field">
              <label>
                Owner <span className="req">*</span>
              </label>
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} disabled={workspace.members.length === 0}>
                {workspace.members.length === 0 && <option value="">— no members —</option>}
                {workspace.members.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {memberLabel(workspace, m.userId)}
                  </option>
                ))}
              </select>
              <div className="hint">Must be a current member of the workspace. Defaults to the workspace creator.</div>
            </div>
          </div>
        </section>
      )}

      {workspace && (
        <section className="panel reveal" data-d="4">
          <header>
            <span className="idx">03</span> Source template
            <span className="hint">optional override</span>
          </header>
          <div className="body">
            <div className="field">
              <label>
                Sample project id <span className="opt">— optional</span>
              </label>
              <input
                type="text"
                placeholder={DEFAULT_SAMPLE_PROJECT_ID}
                spellCheck={false}
                autoComplete="off"
                value={sampleProjectId}
                onChange={(e) => setSampleProjectId(e.target.value)}
              />
              <div className="hint">
                Leave blank to clone the standard demo project (<span className="mono">{DEFAULT_SAMPLE_PROJECT_ID}</span>).
              </div>
            </div>
          </div>
        </section>
      )}

      {workspace && !result && (
        <div className="reveal" data-d="4" style={{ marginTop: 22 }}>
          {!confirming ? (
            <div className="btn-row">
              <button className="btn btn-primary" disabled={!ownerId} onClick={() => setConfirming(true)}>
                <span className="glyph" /> Deploy demo project
              </button>
              <span className="faint mono" style={{ fontSize: 11 }}>
                target ⟶ {workspace.name}
              </span>
            </div>
          ) : (
            <div className="callout warn">
              <span className="mk">confirm</span>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 12 }}>
                  Dispatch demo-project-copier into <b className="mono">{workspace.name}</b> ({workspace.id})? The new
                  project will be owned by <b className="mono">{memberLabel(workspace, ownerId)}</b>
                  {sampleProjectId.trim() ? (
                    <>
                      {" "}
                      from source <b className="mono">{sampleProjectId.trim()}</b>
                    </>
                  ) : null}
                  .
                </div>
                {runError && (
                  <div className="callout error" style={{ marginBottom: 12 }}>
                    <span className="mk">err</span>
                    <span>{runError}</span>
                  </div>
                )}
                <div className="btn-row">
                  <button className={"btn btn-primary" + (running ? " loading" : "")} disabled={running} onClick={dispatch}>
                    <span className="glyph" /> Confirm &amp; deploy
                  </button>
                  <button className="btn btn-ghost" disabled={running} onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <section className="panel reveal" data-d="1" style={{ marginTop: 22 }}>
          <header>
            <span className="idx" style={{ color: "var(--lime)" }}>✓</span> Job dispatched
          </header>
          <div className="body">
            <div className="callout ok" style={{ marginBottom: 16 }}>
              <span className="mk">ok</span>
              <span>The copier job is running. It typically completes within a few minutes — check Seq / Jaeger for progress.</span>
            </div>
            <dl className="spec">
              <dt>job</dt>
              <dd>{result.jobName}</dd>
              <dt>workspace</dt>
              <dd>{result.workspaceId}</dd>
              <dt>owner</dt>
              <dd>{result.projectOwnerId}</dd>
              <dt>source</dt>
              <dd>{result.sampleProjectId}</dd>
            </dl>
            <div className="btn-row" style={{ marginTop: 18 }}>
              <button className="btn" onClick={reset}>
                <span className="glyph" /> New deployment
              </button>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
